import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, FileText, Plus, Save, XCircle } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

function dataHora(valor: unknown) {
  if (!valor) return "-";
  const data = new Date(String(valor));
  return Number.isNaN(data.getTime()) ? "-" : data.toLocaleString("pt-BR");
}

function tocarSom(tipo: "salvo" | "dados") {
  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextClass) return;
  const audioContext = new AudioContextClass();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = tipo === "salvo" ? 880 : 440;
  gain.gain.setValueAtTime(0.001, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.14, audioContext.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + (tipo === "salvo" ? 0.13 : 0.22));
  oscillator.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + (tipo === "salvo" ? 0.13 : 0.22));
  oscillator.addEventListener("ended", () => void audioContext.close());
}

function baixarBase64(arquivoBase64: string, nomeArquivo: string, tipo: string) {
  const bytes = Uint8Array.from(atob(arquivoBase64), (caractere) => caractere.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: tipo }));
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeArquivo;
  link.click();
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === "FINALIZADO" ? "default" : status === "CANCELADO" ? "destructive" : "secondary";
  return <Badge variant={variant}>{status}</Badge>;
}

export default function InventarioCego() {
  const utils = trpc.useUtils();
  const codigoBarrasRef = useRef<HTMLInputElement>(null);
  const [emitenteId, setEmitenteId] = useState("");
  const [inventarioId, setInventarioId] = useState<number | null>(null);
  const [codigoBarras, setCodigoBarras] = useState("");
  const [novoCodigoBarras, setNovoCodigoBarras] = useState("");
  const [novoCodigo, setNovoCodigo] = useState("");
  const [novaDescricao, setNovaDescricao] = useState("");
  const [modalDadosNecessarios, setModalDadosNecessarios] = useState(false);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: inventarios = [], isLoading: carregandoInventarios } = trpc.inventarioCego.listar.useQuery(undefined, { refetchInterval: 5000 });
  const { data: inventario, isLoading: carregandoInventario } = trpc.inventarioCego.obter.useQuery(
    { id: inventarioId ?? 0 },
    { enabled: inventarioId !== null, refetchInterval: 2500 },
  );

  useEffect(() => {
    if (!emitenteId && emitentes.length === 1) setEmitenteId(String(emitentes[0].id));
  }, [emitenteId, emitentes]);

  useEffect(() => {
    if (inventario?.emitenteId) setEmitenteId(String(inventario.emitenteId));
  }, [inventario?.emitenteId]);

  const criar = trpc.inventarioCego.criar.useMutation({
    onSuccess: async (resultado) => {
      setInventarioId(Number(resultado.id));
      await utils.inventarioCego.listar.invalidate();
      toast.success("Inventário criado.");
    },
    onError: (erro) => toast.error(erro.message),
  });

  const bipar = trpc.inventarioCego.bipar.useMutation({
    onSuccess: async (resultado) => {
      if (resultado.novo) {
        tocarSom("dados");
        setNovoCodigoBarras(resultado.codigoBarras);
        setNovoCodigo("");
        setNovaDescricao("");
        setModalDadosNecessarios(true);
        return;
      }
      tocarSom("salvo");
      setCodigoBarras("");
      await Promise.all([utils.inventarioCego.obter.invalidate(), utils.inventarioCego.listar.invalidate()]);
    },
    onError: (erro) => toast.error(erro.message),
  });

  useEffect(() => {
    if (!bipar.isPending && inventario?.status === "PENDENTE" && !modalDadosNecessarios) {
      codigoBarrasRef.current?.focus();
    }
  }, [bipar.isPending, inventario?.status, modalDadosNecessarios]);

  const salvar = trpc.inventarioCego.salvar.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.inventarioCego.obter.invalidate(), utils.inventarioCego.listar.invalidate()]);
      toast.success("Inventário salvo.");
    },
    onError: (erro) => toast.error(erro.message),
  });
  const finalizar = trpc.inventarioCego.finalizar.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.inventarioCego.obter.invalidate(), utils.inventarioCego.listar.invalidate()]);
      toast.success("Inventário finalizado.");
    },
    onError: (erro) => toast.error(erro.message),
  });
  const cancelar = trpc.inventarioCego.cancelar.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.inventarioCego.obter.invalidate(), utils.inventarioCego.listar.invalidate()]);
      toast.success("Inventário cancelado.");
    },
    onError: (erro) => toast.error(erro.message),
  });

  async function biparCodigo() {
    if (!inventarioId || !codigoBarras) return;
    bipar.mutate({ inventarioId, codigoBarras });
  }

  async function salvarItemNovo() {
    if (!inventarioId || !novoCodigo || !novaDescricao) {
      toast.error("Informe código e descrição.");
      return;
    }
    bipar.mutate({ inventarioId, codigoBarras: novoCodigoBarras, codigo: novoCodigo, descricao: novaDescricao });
    setModalDadosNecessarios(false);
    setCodigoBarras("");
    codigoBarrasRef.current?.focus();
  }

  async function exportar(tipo: "excel" | "pdf") {
    if (!inventarioId) return;
    try {
      const resultado = tipo === "excel"
        ? await utils.inventarioCego.exportarExcel.fetch({ id: inventarioId })
        : await utils.inventarioCego.exportarPdf.fetch({ id: inventarioId });
      baixarBase64(resultado.arquivoBase64, resultado.nomeArquivo, tipo === "excel" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf");
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível gerar o relatório.");
    }
  }

  const bloqueado = !inventario || inventario.status !== "PENDENTE";

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Inventário Cego</h1>
        <p className="text-muted-foreground">Bipe as etiquetas. O sistema incrementa a contagem ou solicita os dados do item novo.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Inventários</CardTitle>
            <CardDescription>Selecione um inventário pendente, finalizado ou cancelado.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>Emitente para novo inventário</Label>
              <Select value={emitenteId} onValueChange={setEmitenteId}>
                <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                <SelectContent>
                  {emitentes.map((item: any) => <SelectItem key={item.id} value={String(item.id)}>{item.razaoSocial}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={() => criar.mutate(emitenteId ? { emitenteId: Number(emitenteId) } : {})} disabled={criar.isPending || (!emitenteId && emitentes.length !== 1)}>
              <Plus className="mr-2 h-4 w-4" /> Novo inventário
            </Button>
            <div className="max-h-[520px] space-y-2 overflow-y-auto">
              {carregandoInventarios && <p className="text-sm text-muted-foreground">Carregando...</p>}
              {inventarios.map((item: any) => (
                <button key={item.id} type="button" onClick={() => setInventarioId(Number(item.id))} className={`w-full rounded-md border p-3 text-left ${inventarioId === Number(item.id) ? "border-primary bg-primary/5" : ""}`}>
                  <div className="flex items-center justify-between gap-2"><strong>#{item.id}</strong><StatusBadge status={item.status} /></div>
                  <p className="truncate text-sm">{item.emitenteRazaoSocial || "-"}</p>
                  <p className="text-xs text-muted-foreground">{item.itensDistintos} itens · {item.quantidadeTotal} contados</p>
                </button>
              ))}
              {!carregandoInventarios && !inventarios.length && <p className="text-sm text-muted-foreground">Nenhum inventário.</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle>{inventario ? `Inventário #${inventario.id}` : "Selecione um inventário"}</CardTitle>
                <CardDescription>{inventario ? `${inventario.emitenteRazaoSocial || "-"} · Criado em ${dataHora(inventario.criadoEm)}` : "Crie ou selecione um inventário para começar."}</CardDescription>
              </div>
              {inventario && <StatusBadge status={inventario.status} />}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {inventario && (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Itens distintos</div><div className="text-xl font-semibold">{inventario.itens.length}</div></div>
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Total contado</div><div className="text-xl font-semibold">{inventario.itens.reduce((s: number, i: any) => s + Number(i.quantidade), 0)}</div></div>
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Criado por</div><div className="truncate text-sm font-semibold">{inventario.criadoPorNome || "-"}</div></div>
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Atualizado em</div><div className="text-sm font-semibold">{dataHora(inventario.atualizadoEm)}</div></div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => salvar.mutate({ id: inventario.id })} disabled={bloqueado || salvar.isPending}><Save className="mr-2 h-4 w-4" /> Salvar</Button>
                  <Button onClick={() => finalizar.mutate({ id: inventario.id })} disabled={bloqueado || finalizar.isPending}><CheckCircle2 className="mr-2 h-4 w-4" /> Finalizar</Button>
                  <Button variant="destructive" onClick={() => cancelar.mutate({ id: inventario.id })} disabled={bloqueado || cancelar.isPending}><XCircle className="mr-2 h-4 w-4" /> Cancelar</Button>
                  <Button variant="outline" onClick={() => exportar("excel")} disabled={inventario.status === "CANCELADO"}><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</Button>
                  <Button variant="outline" onClick={() => exportar("pdf")} disabled={inventario.status === "CANCELADO"}><FileText className="mr-2 h-4 w-4" /> PDF</Button>
                </div>

                {inventario.status === "PENDENTE" && (
                  <div className="rounded-md border bg-muted/20 p-4">
                    <Label htmlFor="codigo-barras">Bipe a etiqueta</Label>
                    <div className="mt-2 flex gap-2">
                      <Input ref={codigoBarrasRef} id="codigo-barras" value={codigoBarras} onChange={(e) => setCodigoBarras(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void biparCodigo(); } }} autoFocus placeholder="Código de barras" disabled={bipar.isPending} />
                      <Button onClick={() => void biparCodigo()} disabled={!codigoBarras || bipar.isPending}>Bipar</Button>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Código existente: som salvo. Código novo: som de dados necessários e formulário.</p>
                  </div>
                )}

                {inventario.status === "CANCELADO" && <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"><AlertTriangle className="h-4 w-4" /> Inventário cancelado. Não é possível continuar nem gerar relatório.</div>}
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm"><thead className="bg-muted/50 text-left"><tr><th className="p-2">Código de barras</th><th className="p-2">Código</th><th className="p-2">Descrição</th><th className="p-2 text-right">Quantidade</th><th className="p-2">Atualizado por</th><th className="p-2">Atualizado em</th></tr></thead><tbody>
                    {carregandoInventario ? <tr><td colSpan={6} className="p-4 text-center">Atualizando...</td></tr> : inventario.itens.map((item: any) => <tr key={item.id} className="border-t"><td className="p-2">{item.codigoBarras}</td><td className="p-2">{item.codigo}</td><td className="p-2">{item.descricao}</td><td className="p-2 text-right font-semibold">{item.quantidade}</td><td className="p-2">{item.atualizadoPorNome || "-"}</td><td className="p-2 whitespace-nowrap">{dataHora(item.atualizadoEm)}</td></tr>)}
                    {!carregandoInventario && !inventario.itens.length && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Nenhuma leitura registrada.</td></tr>}
                  </tbody></table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={modalDadosNecessarios} onOpenChange={setModalDadosNecessarios}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dados necessários</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Código de barras novo: <strong>{novoCodigoBarras}</strong>. Informe código e descrição.</p>
          <div className="space-y-3"><div><Label htmlFor="novo-codigo">Código</Label><Input id="novo-codigo" value={novoCodigo} onChange={(e) => setNovoCodigo(e.target.value)} autoFocus /></div><div><Label htmlFor="nova-descricao">Descrição</Label><Input id="nova-descricao" value={novaDescricao} onChange={(e) => setNovaDescricao(e.target.value)} /></div></div>
          <DialogFooter><Button variant="outline" onClick={() => setModalDadosNecessarios(false)}>Fechar</Button><Button onClick={() => void salvarItemNovo()} disabled={bipar.isPending}>Salvar item</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
